import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft,
  Target,
  TrendingUp,
  Users,
  Building,
  Activity,
  Bell,
  Calendar,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  Edit3,
  Save
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SetKPIsPage = () => {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState([
    {
      id: 1,
      metric: 'Child Nutrition Coverage',
      currentValue: 92.4,
      targetValue: 95,
      deadline: '2025-12-31',
      status: 'on-track',
      alertThreshold: 5, // Alert when 5% below target
      unit: '%',
      icon: Activity
    },
    {
      id: 2,
      metric: 'Anganwadi Digitization',
      currentValue: 78.3,
      targetValue: 85,
      deadline: '2025-12-31',
      status: 'on-track',
      alertThreshold: 3,
      unit: '%',
      icon: Building
    },
    {
      id: 3,
      metric: 'Violence Prevention Cases',
      currentValue: 91.0,
      targetValue: 95.0,
      deadline: '2025-12-31',
      status: 'on-track',
      alertThreshold: 2.0,
      unit: '%',
      icon: Users
    }
  ]);

  const [isEditing, setIsEditing] = useState(null);
  const [newKPI, setNewKPI] = useState({
    metric: '',
    targetValue: '',
    deadline: '',
    alertThreshold: '',
    unit: '%'
  });
  const [showAddForm, setShowAddForm] = useState(false);

  const getStatusColor = (status, progress) => {
    if (status === 'ahead' || progress >= 100) return 'text-green-600 bg-green-100';
    if (status === 'on-track' && progress >= 80) return 'text-blue-600 bg-blue-100';
    if (status === 'at-risk' || progress < 70) return 'text-red-600 bg-red-100';
    return 'text-yellow-600 bg-yellow-100';
  };

  const getStatusLabel = (status, progress) => {
    if (progress >= 100) return 'Target Achieved';
    if (status === 'ahead') return 'Ahead of Target';
    if (status === 'on-track') return 'On Track';
    if (status === 'at-risk') return 'At Risk';
    return 'Needs Attention';
  };

  const calculateProgress = (current, target) => {
    return Math.min(100, (current / target) * 100);
  };

  const handleEditKPI = (id) => {
    setIsEditing(id);
  };

  const handleSaveKPI = (id, updatedKPI) => {
    setKpis(kpis.map(kpi => 
      kpi.id === id ? { ...kpi, ...updatedKPI } : kpi
    ));
    setIsEditing(null);
  };

  const handleDeleteKPI = (id) => {
    if (window.confirm('Are you sure you want to delete this KPI?')) {
      setKpis(kpis.filter(kpi => kpi.id !== id));
    }
  };

  const handleAddKPI = () => {
    if (!newKPI.metric || !newKPI.targetValue || !newKPI.deadline) {
      alert('Please fill in all required fields');
      return;
    }

    const kpi = {
      id: Date.now(),
      ...newKPI,
      targetValue: parseFloat(newKPI.targetValue),
      alertThreshold: parseFloat(newKPI.alertThreshold) || 5,
      currentValue: 0, // Will be updated from real data
      status: 'new',
      icon: Target
    };

    setKpis([...kpis, kpi]);
    setNewKPI({ metric: '', targetValue: '', deadline: '', alertThreshold: '', unit: '%' });
    setShowAddForm(false);
  };

  const KPICard = ({ kpi }) => {
    const progress = calculateProgress(kpi.currentValue, kpi.targetValue);
    const Icon = kpi.icon;
    
    if (isEditing === kpi.id) {
      return (
        <Card className="border-orange-200">
          <CardHeader>
            <CardTitle className="text-lg">Edit KPI</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Target Value</label>
              <input
                type="number"
                defaultValue={kpi.targetValue}
                className="w-full p-2 border rounded-md"
                id={`target-${kpi.id}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Deadline</label>
              <input
                type="date"
                defaultValue={kpi.deadline}
                className="w-full p-2 border rounded-md"
                id={`deadline-${kpi.id}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Alert Threshold</label>
              <input
                type="number"
                step="0.1"
                defaultValue={kpi.alertThreshold}
                className="w-full p-2 border rounded-md"
                id={`threshold-${kpi.id}`}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  const updatedKPI = {
                    targetValue: parseFloat(document.getElementById(`target-${kpi.id}`).value),
                    deadline: document.getElementById(`deadline-${kpi.id}`).value,
                    alertThreshold: parseFloat(document.getElementById(`threshold-${kpi.id}`).value)
                  };
                  handleSaveKPI(kpi.id, updatedKPI);
                }}
                className="flex-1 bg-green-500 hover:bg-green-600"
              >
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsEditing(null)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="relative overflow-hidden">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Icon className="w-6 h-6 mr-3 text-orange-500" />
              <CardTitle className="text-lg">{kpi.metric}</CardTitle>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEditKPI(kpi.id)}
              >
                <Edit3 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteKPI(kpi.id)}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Current vs Target */}
            <div className="flex justify-between items-center">
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {kpi.currentValue}{kpi.unit}
                </p>
                <p className="text-sm text-gray-600">Current Value</p>
              </div>
              <div className="text-right">
                <p className="text-xl font-semibold text-orange-600">
                  {kpi.targetValue}{kpi.unit}
                </p>
                <p className="text-sm text-gray-600">Target</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Progress</span>
                <span className="text-sm font-semibold">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-3" />
            </div>

            {/* Status and Details */}
            <div className="space-y-2">
              <Badge className={getStatusColor(kpi.status, progress)}>
                {getStatusLabel(kpi.status, progress)}
              </Badge>
              
              <div className="flex justify-between text-sm text-gray-600">
                <span>Deadline:</span>
                <span>{new Date(kpi.deadline).toLocaleDateString()}</span>
              </div>
              
              <div className="flex justify-between text-sm text-gray-600">
                <span>Alert Threshold:</span>
                <span>{kpi.alertThreshold}{kpi.unit} below target</span>
              </div>
            </div>

            {/* Alert Indicator */}
            {progress < (100 - kpi.alertThreshold) && (
              <div className="flex items-center p-2 bg-yellow-50 rounded-md">
                <Bell className="w-4 h-4 text-yellow-600 mr-2" />
                <span className="text-sm text-yellow-700">
                  Alert: Performance below threshold
                </span>
              </div>
            )}
          </div>
        </CardContent>
        
        {/* Status Indicator Bar */}
        <div className={`absolute bottom-0 left-0 right-0 h-1 ${
          progress >= 100 ? 'bg-green-500' :
          progress >= 80 ? 'bg-blue-500' :
          progress >= 70 ? 'bg-yellow-500' : 'bg-red-500'
        }`} />
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-white hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </Button>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="text-6xl mr-4">
                <Target className="w-16 h-16 text-orange-500" />
              </div>
              <div>
                <h1 className="text-4xl font-bold mb-2">KPI Management</h1>
                <p className="text-xl text-blue-100">Set targets, track progress, and manage key performance indicators</p>
              </div>
            </div>
            
            <Button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add New KPI
            </Button>
          </div>
        </div>
      </section>

      {/* KPI Management */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Add New KPI Form */}
          {showAddForm && (
            <Card className="mb-8 border-orange-200">
              <CardHeader>
                <CardTitle>Add New KPI</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Metric Name</label>
                  <input
                    type="text"
                    value={newKPI.metric}
                    onChange={(e) => setNewKPI({...newKPI, metric: e.target.value})}
                    className="w-full p-2 border rounded-md"
                    placeholder="e.g., Women Skill Training Rate"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Target Value</label>
                  <input
                    type="number"
                    value={newKPI.targetValue}
                    onChange={(e) => setNewKPI({...newKPI, targetValue: e.target.value})}
                    className="w-full p-2 border rounded-md"
                    placeholder="95"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Deadline</label>
                  <input
                    type="date"
                    value={newKPI.deadline}
                    onChange={(e) => setNewKPI({...newKPI, deadline: e.target.value})}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Alert Threshold</label>
                  <input
                    type="number"
                    step="0.1"
                    value={newKPI.alertThreshold}
                    onChange={(e) => setNewKPI({...newKPI, alertThreshold: e.target.value})}
                    className="w-full p-2 border rounded-md"
                    placeholder="5"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    onClick={handleAddKPI}
                    className="flex-1 bg-green-500 hover:bg-green-600"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowAddForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* KPI Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
            <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100">On Track KPIs</p>
                    <p className="text-3xl font-bold">
                      {kpis.filter(kpi => kpi.status === 'on-track' || kpi.status === 'ahead').length}
                    </p>
                  </div>
                  <CheckCircle className="w-12 h-12 text-green-200" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-yellow-100">At Risk KPIs</p>
                    <p className="text-3xl font-bold">
                      {kpis.filter(kpi => kpi.status === 'at-risk').length}
                    </p>
                  </div>
                  <AlertCircle className="w-12 h-12 text-yellow-200" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100">Total KPIs</p>
                    <p className="text-3xl font-bold">{kpis.length}</p>
                  </div>
                  <Target className="w-12 h-12 text-blue-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Individual KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {kpis.map(kpi => (
              <KPICard key={kpi.id} kpi={kpi} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SetKPIsPage;